import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

const SuperAdminContext = createContext();

export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (!context) throw new Error('useSuperAdmin must be used within SuperAdminProvider');
  return context;
};

export const SuperAdminProvider = ({ children }) => {
  const { user, realClaims, loading: authLoading, applyImpersonation } = useAuth();
  const firestore = getFirestore();

  const isSuperAdmin = realClaims?.isSuperAdmin === true; // ← always check REAL claims

  const [impersonation, setImpersonation] = useState(() => {
    try {
      const stored = sessionStorage.getItem('superadmin_impersonation');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const isImpersonating   = Boolean(impersonation?.orgId);
  const impersonatedOrgId = impersonation?.orgId || null;
  const impersonatedRole  = impersonation?.role  || 'headOrgAdmin';

  // ── on mount — if sessionStorage had impersonation, apply it immediately ──
  useEffect(() => {
    if (authLoading) return;
    if (!isSuperAdmin) {
      sessionStorage.removeItem('superadmin_impersonation');
      setImpersonation(null);
      applyImpersonation(null);
      return;
    }
    if (impersonation?.orgId) {
      // restore impersonated claims into AuthContext
      applyImpersonation(_buildClaims(impersonation));
    }
  }, [authLoading, isSuperAdmin]);

  // ── build the fake claims object ─────────────────────────────────────────
  const _buildClaims = (payload) => ({
    ...realClaims,              // keep isSuperAdmin etc on real claims
    orgId:            payload.orgId,
    headOrgId:        payload.orgId,
    role:             payload.role,
    uid:              payload.uid,
    email:            payload.email,
    firstName:        payload.firstName,
    lastName:         payload.lastName,
    _isImpersonating: true,
    _actualRole:      'superAdmin'
  });

  // ── log action ────────────────────────────────────────────────────────────
  const logAction = async (action, details = {}) => {
    if (!isSuperAdmin) return;
    try {
      await addDoc(collection(firestore, 'SuperAdminLogs'), {
        action,
        details,
        userId:           user.uid,
        userEmail:        user.email,
        timestamp:        serverTimestamp(),
        impersonatedOrgId,
        userAgent:        navigator.userAgent
      });
    } catch (error) {
      console.error('Failed to log SuperAdmin action:', error);
    }
  };

  // ── impersonate ───────────────────────────────────────────────────────────
  const impersonateOrg = async (
    orgId,
    role     = 'orgAdmin',
    identity = { firstName: 'super', lastName: 'admin', uid: null, email: null, orgName: '' }
  ) => {
    if (!isSuperAdmin) return;

    const payload = {
      orgId,
      orgName:     identity.orgName   || '',
      role,
      uid:         identity.uid       || null,
      email:       identity.email     || null,
      firstName:   identity.firstName || 'super',
      lastName:    identity.lastName  || 'admin',
      startedAt:   new Date().toISOString()
    };

    sessionStorage.setItem('superadmin_impersonation', JSON.stringify(payload));
    setImpersonation(payload);
    applyImpersonation(_buildClaims(payload));  // ← THIS is what makes the app refetch

    await logAction('IMPERSONATE_ORG', { orgId, role, targetUid: payload.uid });
  };

  // ── stop ──────────────────────────────────────────────────────────────────
  const stopImpersonation = async () => {
    const previous = impersonation;
    sessionStorage.removeItem('superadmin_impersonation');
    setImpersonation(null);
    applyImpersonation(null);   // ← restores real claims

    await logAction('STOP_IMPERSONATION', {
      previousOrgId: previous?.orgId || null,
      previousUid:   previous?.uid   || null
    });
  };

  const value = {
    isSuperAdmin,
    isImpersonating,
    impersonation,
    impersonatedOrgId,
    impersonatedRole,
    impersonateOrg,
    stopImpersonation,
    logAction
  };

  return (
    <SuperAdminContext.Provider value={value}>
      {children}
    </SuperAdminContext.Provider>
  );
};